<template>
    <div class="hidden lg:flex grow items-center justify-center">
        <!-- New Order -->
        <router-link
            v-if="store.getters['account/can']('order:place')"
            ref="new_order"
            :to="{
                name: 'order-lenses', params: {
                    lang: $route.params.lang,
                    entity_id: $route.params.entity_id,
                },
            }"
            class="sl-navbar__link-item"
            @click="trackNav('new_order')"
        >
            {{ $t('main_menu.new_order') }}
        </router-link>

        <div class="sl-navbar__separator"></div>

        <!-- My Orders -->
        <router-link
            v-if="store.getters['account/can']('order:view')"
            :to="{ name: 'order-list', params: $route.params }"
            class="sl-navbar__link-item"
        >
            {{ $t('main_menu.orders') }}
        </router-link>

        <div class="sl-navbar__separator"></div>

        <!-- Review Requests -->
        <router-link
            :to="{ name: 'entity-review-requests', params: $route.params }"
            class="sl-navbar__link-item"
            :class="{ 'sl-navbar__link-item--alert': hasUnreadReviewComments }"
        >
            {{ $t('main_menu.review_requests') }}
        </router-link>

        <div
            v-if="canAccessInvoicing"
            class="sl-navbar__separator"
        >
        </div>

        <!-- Invoicing -->
        <router-link
            v-if="canAccessInvoicing"
            :to="{ name: 'invoice-list', params: $route.params }"
            class="sl-navbar__link-item"
        >
            {{ $t('main_menu.invoicing') }}
        </router-link>

        <div
            v-if="store.getters['account/can']('price:view')"
            class="sl-navbar__separator"
        >
        </div>

        <!-- Price List -->
        <router-link
            v-if="store.getters['account/can']('price:view')"
            :to="{ name: 'price-list', params: $route.params }"
            class="sl-navbar__link-item"
        >
            {{ $t('side_menu.price_list') }}
        </router-link>

        <div v-if="canAccessReturns" class="sl-navbar__separator"></div>

        <!-- Returns -->
        <router-link
            v-if="canAccessReturns"
            :to="{
                name: 'return-lenses',
                params: {
                    lang: $route.params.lang,
                    entity_id: $route.params.entity_id,
                },
            }"
            class="sl-navbar__link-item"
        >
            {{ $t('main_menu.return_an_article') }}

            <div
                v-if="returnBasketLines.length"
                class="sl-return-basket__count-wrapper"
            >
                <div class="sl-return-basket__count">
                    {{ returnBasketLines.length }}
                </div>
            </div>
        </router-link>
    </div>
</template>

<script setup>
import {
    computed,
    onBeforeUnmount,
    onMounted,
    ref,
    useTemplateRef,
    watch,
} from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { event } from 'vue-gtag';
import { getUserConfigurations } from '@/vue-configurations';
import ReviewRequest from '@/models/ReviewRequest';
import { DEFAULT_MAIN_MENU_ITEM } from '@/store/mainStore';

// ------------------------------------------------------------ TEMPLATE REFS

/** @type {Record<string, import("vue").TemplateRef} */
const refMap = {};
const refs = ['new_order'];
for(const refName of refs)
{
    refMap[refName] = useTemplateRef(refName);
}

// ------------------------------------------------------------ COMPOSABLES

const store = useStore();
const route = useRoute();
const uconfig = getUserConfigurations();

// ------------------------------------------------------------ DATA

const now = ref(0);
const nowInterval = ref(0);

// ------------------------------------------------------------ COMPUTED

const cEntity = computed(() => store.state.account.cEntity);
const returnBasketLines = computed(() => store.state.returnBasket.lines);

const canAccessReturns = computed(() =>
{

    if(['MD', 'UA'].includes(cEntity.value.country))
    {
        return false;
    }

    return store.getters['account/can']('return:create');
});

const canAccessInvoicing = computed(() =>
{
    const { type, country } = cEntity.value;
    if(type !== 'DISTRIBUTOR' && ['MD', 'UA'].includes(country))
    {
        return false;
    }

    return store.getters['account/can']('invoice:view');
});

const lastReadByReviewRequest = computed(() =>
{
    now.value; // HACK: force recalculate every second
    const out = {};

    const cfg = uconfig.all().review_comments ?? {};
    for(const requestId in cfg)
    {
        out[requestId] = cfg[requestId].last_read ?? 0;
    }

    return out;
});

const hasUnreadReviewComments = computed(() =>
{
    /** @type {ReviewRequest[]} */
    const requests = store.state.orders.waitingForReview;
    for(const r of requests)
    {
        // Only consider the review requests of the current entity
        if(r.entity.id !== store.state.account.cEntity.id)
        {
            continue;
        }

        const lastCommentDate = store.state.orders.lastCommentDatesByOrder.get(
            r.order.reference
        ) ?? 0;
        const lastRead = lastReadByReviewRequest.value[r.id] ?? 0;
        if(lastRead < lastCommentDate)
        {
            return true;
        }
    }

    return false;
});

// ------------------------------------------------------------ WATCHERS

watch(now, () => setCurrentMainMenuElement());

// ------------------------------------------------------------ METHODS

/**
 * Make the current main menu HTML element accessible via the store.
 */
function setCurrentMainMenuElement()
{
    /** @type {string} */
    const mainMenu = route.meta.mainMenu || DEFAULT_MAIN_MENU_ITEM;

    const cMainMenuElement = refMap[mainMenu];
    if(!cMainMenuElement)
    {
        throw new ReferenceError(
            `main menu element not registered: ${mainMenu}`
        );
    }

    store.dispatch('setCurrentMainMenuElement', cMainMenuElement.value);
}

/**
 * @param {string} item
 */
function trackNav(item)
{
    const eventName = 'nav-' + item.toLowerCase();
    event(eventName);
}

function updateNow()
{
    now.value = Math.floor(new Date().getTime() / 1000);
}

// ------------------------------------------------------------ LIFECYCLE HOOKS

onMounted(() =>
{
    updateNow();
    nowInterval.value = setInterval(updateNow, 1000);

});

onBeforeUnmount(() => clearInterval(nowInterval.value));
</script>

<style scoped>
@reference "#main.css";

.sl-navbar__link-item
{
    @apply mx-3 inline-block text-white text-sm;
}

.sl-navbar__link-item--alert
{
    @apply relative flex gap-1 items-center;

    &::after
    {
        content: "";
        @apply block rounded-full shadow w-2 h-2 bg-yellow-500;
    }
}

.sl-return-basket__count-wrapper
{
    right: -16px;
    top: -6px;
    width: 18px;
    height: 18px;
    @apply absolute flex items-center justify-center bg-yellow-400 rounded-full;
}

.sl-return-basket__count
{
    font-size: 0.675rem;
    transform: translateY(1px);
    @apply text-primary-500;
}
</style>
